// src/pages/portal/PortalVoidsPage.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { format, subDays, subMonths, subYears } from 'date-fns';
import PortalLayout, { NAV_FEATURE_MAP, NAV_PERMISSION_MAP } from './PortalLayout';
import { voidService } from '../../services/api';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import useIsMobile from '../../hooks/useIsMobile';
import MobileCard from '../../components/common/MobileCard';
import MobileBanner from '../../components/common/MobileBanner';
import PortalLinkTabs, { buildTransactionsVoidsTabs } from '../../components/common/PortalLinkTabs';
import { C } from '../../styles/theme';

// ─── Brand tokens ─────────────────────────────────────────────────────────────

const FONT = "'Plus Jakarta Sans', sans-serif";

// ─── Void type → category mapping ─────────────────────────────────────────────
const FINANCIAL_TYPES = ['transaction', 'expense', 'salary_advance', 'salary_payment'];
const OPERATIONAL_TYPES = ['pending_order', 'order_tracking'];
const STAFF_TYPES = ['employee_deactivation', 'staff_allowance'];

const CATEGORY_FILTERS = {
    all:         [...FINANCIAL_TYPES, ...OPERATIONAL_TYPES, ...STAFF_TYPES],
    financial:   FINANCIAL_TYPES,
    operational: OPERATIONAL_TYPES,
    staff:       STAFF_TYPES,
};

const TYPE_LABELS = {
    transaction:            'Transaction',
    expense:                'Expense',
    salary_advance:         'Salary Advance',
    salary_payment:         'Salary Payment',
    pending_order:          'Pending Order',
    order_tracking:         'Order Tracking',
    employee_deactivation:  'Employee Deactivation',
    staff_allowance:        'Staff Allowance',
};

const TYPE_COLOURS = {
    transaction:            { color: C.green,  bg: C.greenLight },
    expense:                { color: C.green,  bg: C.greenLight },
    salary_advance:         { color: C.green,  bg: C.greenLight },
    salary_payment:         { color: C.green,  bg: C.greenLight },
    pending_order:          { color: C.blue,   bg: C.blueLight },
    order_tracking:         { color: C.blue,   bg: C.blueLight },
    employee_deactivation:  { color: C.amber,  bg: C.amberLight },
    staff_allowance:        { color: C.amber,  bg: C.amberLight },
};

// ─── Shared components ────────────────────────────────────────────────────────
const Badge = ({ children, color, bg }) => (
    <span style={{
        fontSize: 11, fontWeight: 700, letterSpacing: 0.4,
        borderRadius: 5, padding: '2px 8px',
        background: bg, color, display: 'inline-block',
        fontFamily: FONT,
    }}>
        {children}
    </span>
);

const EmptyState = ({ icon, title, subtitle }) => (
    <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', padding: '48px 24px', color: C.muted,
        fontFamily: FONT,
    }}>
        <div style={{ marginBottom: 12, opacity: 0.4 }}>{icon}</div>
        <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 4 }}>{title}</div>
        <div style={{ fontSize: 13, color: C.mutedLight }}>{subtitle}</div>
    </div>
);

const SectionHeader = ({ title, count, collapsed, onToggle }) => (
    <div
        onClick={onToggle}
        style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '12px 16px', cursor: 'pointer', userSelect: 'none',
            borderBottom: `1px solid ${C.border}`,
        }}
    >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.muted}
                strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                style={{ transform: collapsed ? 'rotate(-90deg)' : 'rotate(0)', transition: 'transform 0.2s' }}>
                <polyline points="6 9 12 15 18 9" />
            </svg>
            <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: C.muted, fontFamily: FONT }}>
                {title}
            </span>
            {count > 0 && (
                <span style={{
                    fontSize: 10, fontWeight: 700, background: C.greenLight, color: C.green,
                    borderRadius: 10, padding: '1px 7px', fontFamily: FONT,
                }}>
                    {count}
                </span>
            )}
        </div>
    </div>
);

// ─── Helpers ──────────────────────────────────────────────────────────────────
const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
        const d = new Date(dateStr);
        return format(d, 'dd MMM yyyy, HH:mm');
    } catch {
        return dateStr;
    }
};

const fmtAmt = (amount, currency) => {
    if (amount == null || isNaN(Number(amount)) || Number(amount) === 0) return '—';
    return `${currency} ${Number(amount).toFixed(2)}`;
};

// ─── Type-aware extractors ────────────────────────────────────────────────────

const extractAmount = (rd, voidType) => {
    switch (voidType) {
        case 'transaction':
            return rd.final_amount || rd.total_amount || null;
        case 'expense':
            return rd.total_cost || null;
        case 'salary_advance':
        case 'salary_payment':
            return rd.amount || null;
        case 'staff_allowance':
            return rd.total_amount || (rd.quantity && rd.unit_price ? rd.quantity * rd.unit_price : null);
        case 'pending_order':
            return rd.total_amount || null;
        case 'order_tracking':
            return null;
        default:
            return rd.final_amount || rd.total_amount || rd.total_cost || rd.amount || null;
    }
};

const extractPaymentMethod = (rd, voidType, t) => {
    if (voidType === 'transaction') return rd.payment_method || null;
    if (voidType === 'expense') {
        const expType = rd.expense_type === 'overhead' ? t('overhead') : rd.expense_type === 'daily' ? t('daily') : null;
        return [expType, rd.category].filter(Boolean).join(' — ') || null;
    }
    if (voidType === 'salary_advance') return t('advance');
    if (voidType === 'salary_payment') return rd.period || t('salary');
    return rd.payment_method || null;
};

const extractDetails = (rd, voidType) => {
    switch (voidType) {
        case 'transaction':
            return rd.products_summary
                || (rd.items && rd.items.length > 0
                    ? rd.items.map(i => `${i.quantity || 1}x ${i.product_name || 'Item'}`).join(', ')
                    : null)
                || `${rd.dining_option || ''} #${rd.table_number || ''}`.trim()
                || null;
        case 'expense':
            return [
                rd.category,
                rd.product && `${rd.quantity || 1}x ${rd.product}`,
                rd.price_per_unit && `@ ${Number(rd.price_per_unit).toFixed(2)}/unit`,
                rd.miscellaneous,
            ].filter(Boolean).join(' — ') || null;
        case 'salary_advance':
            return [rd.notes, rd.associated_expense?.product].filter(Boolean).join(' — ') || null;
        case 'salary_payment':
            return rd.period || null;
        case 'staff_allowance':
            return `${rd.quantity || 1}x ${rd.product_name || rd.product_code || 'Item'}` +
                (rd.unit_price ? ` @ ${Number(rd.unit_price).toFixed(2)}` : '');
        case 'pending_order':
            return rd.items_summary || rd.order_key || null;
        case 'order_tracking': {
            const items = rd.items;
            if (items && Array.isArray(items) && items.length > 0) {
                return items.map(i => `${i.quantity || 1}x ${i.product_name || i.name || 'Item'}`).join(', ');
            }
            return `${rd.total_items_count || 0} items`;
        }
        default:
            return null;
    }
};

const extractDiningOption = (rd, voidType) => {
    if (voidType === 'pending_order') return rd.dining_option || rd.order_key?.split('-')[0] || null;
    if (voidType === 'order_tracking') return rd.table_info?.dining_option || rd.category || null;
    return rd.dining_option || null;
};

const extractTableOrOrder = (rd, voidType) => {
    if (voidType === 'pending_order') return rd.table_number || rd.order_key || null;
    if (voidType === 'order_tracking') {
        const ti = rd.table_info || {};
        return ti.table_number ? `#${ti.table_number}` : rd.tracking_id || null;
    }
    return rd.table_number || rd.tracking_id || null;
};

const extractOperationalDetails = (rd, voidType) => {
    if (voidType === 'order_tracking') {
        const items = rd.items;
        if (items && Array.isArray(items) && items.length > 0) {
            return items.map(i => `${i.quantity || 1}x ${i.product_name || i.name || 'Item'}`).join(', ');
        }
        return `${rd.total_items_count || 0} items`;
    }
    if (voidType === 'pending_order') {
        return rd.items_summary || rd.order_key || '—';
    }
    return '—';
};

const extractStaffName = (rd, voidType) => {
    if (voidType === 'pending_order') return rd.staff_name || null;
    if (voidType === 'order_tracking') return rd.table_info?.staff_name || null;
    return null;
};

const extractEmployeeName = (rd) => {
    return rd.employee_name || rd.name || rd.display_name || rd.employee_id || null;
};

const extractRole = (rd, voidType) => {
    if (voidType === 'staff_allowance') return rd.product_name || rd.product_code || null;
    return rd.role || rd.role_name || null;
};

// ─── Checks if an order_tracking void is a child of a pending_order void ─────
const isChildTracking = (otVoid, pendingVoids) => {
    const reason = otVoid.reason || '';
    return pendingVoids.some(pv => {
        const orderKey = pv.record_data?.order_key || pv.record_id;
        return reason.includes(orderKey);
    });
};

// ─── Expandable detail panel ──────────────────────────────────────────────────
const DETAIL_LABEL_STYLE = { fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5, minWidth: 120 };
const DETAIL_VALUE_STYLE = { fontSize: 13, color: C.text };

const DetailRow = ({ label, value }) => {
    if (value == null || value === '' || value === '—') return null;
    return (
        <div style={{ display: 'flex', gap: 12, padding: '4px 0' }}>
            <span style={DETAIL_LABEL_STYLE}>{label}</span>
            <span style={DETAIL_VALUE_STYLE}>{value}</span>
        </div>
    );
};

const VoidDetailPanel = ({ v, currency, t, childTrackings = [] }) => {
    const rd = v.record_data || {};
    const vt = v.void_type;

    const renderItems = (items) => {
        if (!items || !Array.isArray(items) || items.length === 0) return null;
        return (
            <div style={{ marginTop: 6 }}>
                <div style={DETAIL_LABEL_STYLE}>{t('voids_items')}</div>
                <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {items.map((item, i) => (
                        <div key={i} style={{ fontSize: 13, color: C.text, padding: '2px 0 2px 12px', borderLeft: `2px solid ${C.border}` }}>
                            {item.quantity || 1}x {item.product_name || item.name || 'Item'}
                            {item.unit_price ? ` — ${currency} ${Number(item.unit_price).toFixed(2)}` : ''}
                            {item.total_amount ? ` = ${currency} ${Number(item.total_amount).toFixed(2)}` : ''}
                            {item.notes ? ` (${item.notes})` : ''}
                            {item.add_ons && item.add_ons.length > 0 ? ` + ${item.add_ons.map(a => a.name || a).join(', ')}` : ''}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div style={{
            padding: '12px 14px 12px 28px', background: '#f8fafc',
            borderBottom: `1px solid ${C.border}`, fontFamily: FONT,
        }}>
            <div className="portal-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 32px', maxWidth: 700 }}>
                <DetailRow label={t('voids_date')} value={formatDate(v.deleted_at)} />
                <DetailRow label={t('voids_authorized_by')} value={v.authorized_by} />

                {vt === 'transaction' && <>
                    <DetailRow label={t('transaction_id')} value={rd.transaction_id} />
                    <DetailRow label={t('voids_payment_method')} value={rd.payment_method} />
                    <DetailRow label={t('dining')} value={rd.dining_option} />
                    <DetailRow label={t('table')} value={rd.table_number} />
                    <DetailRow label={t('staff')} value={rd.staff_name} />
                    <DetailRow label={t('subtotal')} value={rd.total_amount != null ? `${currency} ${Number(rd.total_amount).toFixed(2)}` : null} />
                    <DetailRow label={t('discount')} value={rd.discount_amount ? `${currency} ${Number(rd.discount_amount).toFixed(2)}` : null} />
                    <DetailRow label={t('final')} value={rd.final_amount != null ? `${currency} ${Number(rd.final_amount).toFixed(2)}` : null} />
                    <DetailRow label={t('cash')} value={rd.cash_amount ? `${currency} ${Number(rd.cash_amount).toFixed(2)}` : null} />
                    <DetailRow label={t('card')} value={rd.card_amount ? `${currency} ${Number(rd.card_amount).toFixed(2)}` : null} />
                    <DetailRow label={t('notes')} value={rd.notes} />
                </>}

                {vt === 'expense' && <>
                    <DetailRow label={t('expense_id')} value={rd.expense_id} />
                    <DetailRow label={t('type')} value={rd.expense_type === 'overhead' ? t('overhead') : rd.expense_type === 'daily' ? t('daily') : '—'} />
                    <DetailRow label={t('category')} value={rd.category} />
                    <DetailRow label={t('product')} value={rd.product} />
                    <DetailRow label={t('quantity')} value={rd.quantity} />
                    <DetailRow label={t('price_per_unit')} value={rd.price_per_unit != null ? `${currency} ${Number(rd.price_per_unit).toFixed(2)}` : null} />
                    <DetailRow label={t('total')} value={rd.total_cost != null ? `${currency} ${Number(rd.total_cost).toFixed(2)}` : null} />
                    <DetailRow label={t('authorizer')} value={rd.authorizer} />
                    <DetailRow label={t('notes')} value={rd.miscellaneous} />
                </>}

                {(vt === 'salary_advance' || vt === 'salary_payment') && <>
                    <DetailRow label={t('employee_id')} value={rd.employee_id} />
                    <DetailRow label={t('amount')} value={rd.amount != null ? `${currency} ${Number(rd.amount).toFixed(2)}` : null} />
                    <DetailRow label={t('period')} value={rd.period} />
                    <DetailRow label={t('notes')} value={rd.notes} />
                </>}

                {vt === 'pending_order' && <>
                    <DetailRow label={t('order')} value={rd.order_key} />
                    <DetailRow label={t('dining')} value={rd.dining_option} />
                    <DetailRow label={t('table')} value={rd.table_number} />
                    <DetailRow label={t('staff')} value={rd.staff_name} />
                    <DetailRow label={t('amount')} value={rd.total_amount ? `${currency} ${Number(rd.total_amount).toFixed(2)}` : null} />
                    <DetailRow label={t('discount')} value={rd.discount ? `${currency} ${Number(rd.discount).toFixed(2)}` : null} />
                    <DetailRow label={t('items')} value={rd.items_summary} />
                    <DetailRow label={t('items_count')} value={rd.items_count} />
                </>}

                {vt === 'order_tracking' && <>
                    <DetailRow label={t('tracking_id')} value={rd.tracking_id} />
                    <DetailRow label={t('category')} value={rd.category} />
                    <DetailRow label={t('status')} value={rd.status} />
                    <DetailRow label={t('dining')} value={rd.table_info?.dining_option} />
                    <DetailRow label={t('table')} value={rd.table_info?.table_number} />
                    <DetailRow label={t('staff')} value={rd.table_info?.staff_name} />
                </>}

                {vt === 'employee_deactivation' && <>
                    <DetailRow label={t('employee')} value={rd.name || rd.employee_name} />
                    <DetailRow label={t('employee_id')} value={rd.employee_id} />
                    <DetailRow label={t('role')} value={rd.role} />
                    <DetailRow label={t('previous_status')} value={rd.previous_status} />
                </>}

                {vt === 'staff_allowance' && <>
                    <DetailRow label={t('employee_id')} value={rd.employee_id} />
                    <DetailRow label={t('product')} value={rd.product_name || rd.product_code} />
                    <DetailRow label={t('quantity')} value={rd.quantity} />
                    <DetailRow label={t('unit_price')} value={rd.unit_price != null ? `${currency} ${Number(rd.unit_price).toFixed(2)}` : null} />
                    <DetailRow label={t('total')} value={rd.total_amount != null ? `${currency} ${Number(rd.total_amount).toFixed(2)}` : null} />
                </>}

                <DetailRow label={t('voids_reason')} value={v.reason} />
            </div>

            {/* Transaction items */}
            {vt === 'transaction' && renderItems(rd.items)}

            {/* Order tracking items */}
            {vt === 'order_tracking' && renderItems(rd.items)}

            {/* Child tracking records for pending orders */}
            {childTrackings.length > 0 && (
                <div style={{ marginTop: 10 }}>
                    <div style={{ ...DETAIL_LABEL_STYLE, marginBottom: 6 }}>{t('kds_records_voided')}</div>
                    {childTrackings.map(ct => {
                        const crd = ct.record_data || {};
                        const citems = crd.items || [];
                        return (
                            <div key={ct.void_id} style={{
                                padding: '6px 12px', marginBottom: 4, borderLeft: `2px solid ${C.blue}`,
                                background: C.blueLight, borderRadius: '0 6px 6px 0', fontSize: 12,
                            }}>
                                <span style={{ fontWeight: 600 }}>{crd.category || t('kds')}</span>
                                <span style={{ color: C.muted, marginLeft: 8 }}>
                                    {citems.length > 0
                                        ? citems.map(i => `${i.quantity || 1}x ${i.name || i.product_name || 'Item'}`).join(', ')
                                        : `${crd.total_items_count || 0} items`}
                                </span>
                                <span style={{ color: C.mutedLight, marginLeft: 8 }}>({crd.tracking_id})</span>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

// ─── Mobile void card ─────────────────────────────────────────────────────────
const MobileVoidCard = ({ v, currency, t, kind, childTrackings = [] }) => {
    const rd = v.record_data || {};
    const vt = v.void_type;
    const typeLabel = t(`void_type_${vt}`) || TYPE_LABELS[vt] || vt;
    const amount = fmtAmt(extractAmount(rd, vt), currency);

    let title, subtitleParts;
    if (kind === 'operational') {
        title = `${typeLabel}${extractTableOrOrder(rd, vt) ? ` · ${extractTableOrOrder(rd, vt)}` : ''}`;
        subtitleParts = [extractDiningOption(rd, vt), extractStaffName(rd, vt) || v.authorized_by, formatDate(v.deleted_at)];
    } else {
        title = extractDetails(rd, vt) || typeLabel;
        subtitleParts = [extractPaymentMethod(rd, vt, t), v.authorized_by, formatDate(v.deleted_at)];
    }
    const subtitle = subtitleParts.filter(Boolean).join(' · ');

    return (
        <MobileCard
            accent="red"
            avatar={{ initials: typeLabel.slice(0, 2).toUpperCase(), tone: 'red' }}
            title={title}
            amount={amount !== '—' ? amount : undefined}
            status={{ label: typeLabel, tone: 'red' }}
            subtitle={subtitle}
        >
            <VoidDetailPanel v={v} currency={currency} t={t} childTrackings={childTrackings} />
        </MobileCard>
    );
};

// ─── Main component ───────────────────────────────────────────────────────────
const PortalVoidsPage = () => {
    const { t } = useLanguage();
    const { getCurrency, hasPermission, hasFeature } = useAuth();
    const currency = getCurrency();
    const linkTabs = buildTransactionsVoidsTabs({ t, hasPermission, hasFeature, navFeatureMap: NAV_FEATURE_MAP, navPermissionMap: NAV_PERMISSION_MAP });
    const isMobile = useIsMobile();
    const [voids, setVoids] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeCategory, setActiveCategory] = useState('all');
    const [datePreset, setDatePreset] = useState('week');
    const [customStart, setCustomStart] = useState(format(subDays(new Date(), 7), 'yyyy-MM-dd'));
    const [customEnd, setCustomEnd] = useState(format(new Date(), 'yyyy-MM-dd'));
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [collapsedSections, setCollapsedSections] = useState({});
    const [expandedRow, setExpandedRow] = useState(null);
    const pageSize = 50;

    const getDateRange = useCallback(() => {
        const today = new Date();
        const end = format(today, 'yyyy-MM-dd');
        switch (datePreset) {
            case 'today': return { start: end, end };
            case 'week': return { start: format(subDays(today, 7), 'yyyy-MM-dd'), end };
            case 'month': return { start: format(subMonths(today, 1), 'yyyy-MM-dd'), end };
            case 'year': return { start: format(subYears(today, 1), 'yyyy-MM-dd'), end };
            case 'custom': return { start: customStart, end: customEnd };
            default: return { start: format(subDays(today, 7), 'yyyy-MM-dd'), end };
        }
    }, [datePreset, customStart, customEnd]);

    const fetchVoids = useCallback(async () => {
        try {
            setLoading(true);
            const types = CATEGORY_FILTERS[activeCategory].join(',');
            const { start, end } = getDateRange();
            const params = {
                void_type: types,
                start_date: start + 'T00:00:00',
                end_date: end + 'T23:59:59',
                page,
                page_size: pageSize,
            };
            const data = await voidService.getVoids(params);
            setVoids(data.items || []);
            setTotal(data.total || 0);
        } catch (err) {
            console.error('Error fetching voids:', err);
            setVoids([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    }, [activeCategory, getDateRange, page]);

    useEffect(() => { fetchVoids(); }, [fetchVoids]);
    useEffect(() => { setPage(1); }, [activeCategory, datePreset, customStart, customEnd]);

    const toggleSection = (key) => {
        setCollapsedSections(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const toggleExpand = (voidId) => {
        setExpandedRow(prev => prev === voidId ? null : voidId);
    };

    const financialVoids = voids.filter(v => FINANCIAL_TYPES.includes(v.void_type));
    const pendingOrderVoids = voids.filter(v => v.void_type === 'pending_order');
    const allOrderTrackingVoids = voids.filter(v => v.void_type === 'order_tracking');

    // Separate standalone order_tracking from ones that are children of pending_order voids
    const operationalVoids = useMemo(() => {
        const standaloneOT = allOrderTrackingVoids.filter(ot => !isChildTracking(ot, pendingOrderVoids));
        return [...pendingOrderVoids, ...standaloneOT];
    }, [pendingOrderVoids, allOrderTrackingVoids]);

    // Build a map of pending order key → child tracking voids
    const childTrackingMap = useMemo(() => {
        const map = {};
        pendingOrderVoids.forEach(pv => {
            const orderKey = pv.record_data?.order_key || pv.record_id;
            map[pv.void_id] = allOrderTrackingVoids.filter(ot =>
                (ot.reason || '').includes(orderKey)
            );
        });
        return map;
    }, [pendingOrderVoids, allOrderTrackingVoids]);

    const staffVoids = voids.filter(v => STAFF_TYPES.includes(v.void_type));
    const totalPages = Math.ceil(total / pageSize);

    // ─── Summary stats ────────────────────────────────────────────────────
    const stats = useMemo(() => {
        let financial = 0, operational = 0, staff = 0;
        voids.forEach(v => {
            const amt = extractAmount(v.record_data || {}, v.void_type) || 0;
            if (FINANCIAL_TYPES.includes(v.void_type)) financial += Number(amt);
            else if (OPERATIONAL_TYPES.includes(v.void_type)) operational += Number(amt);
            else if (STAFF_TYPES.includes(v.void_type)) staff += Number(amt);
        });
        return { financial, operational, staff, total: financial + operational + staff };
    }, [voids]);

    // ─── Category filter pills ────────────────────────────────────────────
    const categories = [
        { key: 'all', label: t('voids_all') },
        { key: 'financial', label: t('voids_financial') },
        { key: 'operational', label: t('voids_operational') },
        { key: 'staff', label: t('voids_staff') },
    ];

    const noVoidsIcon = (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" /><line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
        </svg>
    );
    const financialIcon = (
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
        </svg>
    );
    const operationalIcon = (
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" />
        </svg>
    );
    const staffIcon = (
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
        </svg>
    );

    const rowClickStyle = { cursor: 'pointer' };

    return (
        <PortalLayout>
            <div style={{ fontFamily: FONT, color: C.text }}>
                <PortalLinkTabs tabs={linkTabs} active="voids" />

                {/* ─── Page header ─────────────────────────────────────────────── */}
                <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    marginBottom: 20, flexWrap: 'wrap', gap: 12,
                }}>
                    <div>
                        <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0, color: C.text }}>
                            {t('voids_title')}
                        </h1>
                        <p style={{ fontSize: 13, color: C.muted, margin: '4px 0 0' }}>
                            {t('voids_subtitle')}
                        </p>
                    </div>
                </div>

                {/* ─── Mobile banner: total voids in range ─────────────────────── */}
                {isMobile && !loading && voids.length > 0 && (
                    <MobileBanner
                        tone="bad"
                        icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="4.93" y1="4.93" x2="19.07" y2="19.07" /></svg>}
                        title={`${total} ${total === 1 ? (t('void') || 'void') : (t('voids_title') || 'Voids')}`}
                        subtitle={stats.total > 0 ? `${currency} ${stats.total.toFixed(2)} ${t('voids_voided_value') || 'voided value'}` : (t('voids_subtitle') || undefined)}
                    />
                )}

                {/* ─── Summary stats ───────────────────────────────────────────── */}
                {!isMobile && !loading && voids.length > 0 && (
                    <div style={{
                        display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap',
                    }}>
                        {[
                            { label: t('voids_financial'), value: stats.financial, color: C.green, bg: C.greenLight },
                            { label: t('voids_operational'), value: stats.operational, color: C.blue, bg: C.blueLight },
                            { label: t('voids_staff'), value: stats.staff, color: C.amber, bg: C.amberLight },
                        ].map(s => (
                            <div key={s.label} style={{
                                flex: 1, minWidth: 140, padding: '14px 16px', borderRadius: 10,
                                background: C.white, border: `1.5px solid ${C.border}`,
                            }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 4 }}>
                                    {s.label}
                                </div>
                                <div style={{ fontSize: 20, fontWeight: 800, color: s.color }}>
                                    {s.value > 0 ? `${currency} ${s.value.toFixed(2)}` : '—'}
                                </div>
                            </div>
                        ))}
                        <div style={{
                            flex: 1, minWidth: 140, padding: '14px 16px', borderRadius: 10,
                            background: C.white, border: `1.5px solid ${C.border}`,
                        }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 4 }}>
                                {t('voids_all')}
                            </div>
                            <div style={{ fontSize: 20, fontWeight: 800, color: C.text }}>
                                {stats.total > 0 ? `${currency} ${stats.total.toFixed(2)}` : '—'}
                            </div>
                        </div>
                    </div>
                )}

                {/* ─── Filters bar (mobile) ────────────────────────────────────── */}
                {isMobile ? (
                    <div style={{ marginBottom: 16 }}>
                        {/* Category segmented */}
                        <div style={{ display: 'flex', gap: 4, background: '#f1f5f9', borderRadius: 9, padding: 3, marginBottom: 10 }}>
                            {categories.map(c => (
                                <button key={c.key} onClick={() => setActiveCategory(c.key)}
                                    style={{ flex: 1, padding: '8px 6px', borderRadius: 7, border: 'none', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: FONT, background: activeCategory === c.key ? C.white : 'transparent', color: activeCategory === c.key ? C.green : C.muted, boxShadow: activeCategory === c.key ? '0 1px 3px rgba(0,0,0,0.1)' : 'none' }}>
                                    {c.label}
                                </button>
                            ))}
                        </div>
                        {/* Period pills */}
                        <div className="portal-hscroll" style={{ display: 'flex', gap: 6, paddingBottom: 2 }}>
                            {[
                                { key: 'today', label: t('voids_today') },
                                { key: 'week', label: t('voids_past_week') },
                                { key: 'month', label: t('voids_past_month') },
                                { key: 'year', label: t('voids_past_year') },
                                { key: 'custom', label: t('voids_custom') },
                            ].map(p => (
                                <button key={p.key} onClick={() => setDatePreset(p.key)}
                                    style={{ flexShrink: 0, padding: '8px 14px', borderRadius: 20, border: `1.5px solid ${datePreset === p.key ? C.green : C.border}`, background: datePreset === p.key ? C.green : C.white, color: datePreset === p.key ? '#fff' : C.muted, fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap', cursor: 'pointer', fontFamily: FONT }}>
                                    {p.label}
                                </button>
                            ))}
                        </div>
                        {datePreset === 'custom' && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
                                <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)}
                                    style={{ flex: 1, minWidth: 0, padding: '9px 10px', borderRadius: 8, border: `1.5px solid ${C.border}`, fontSize: 13, fontFamily: FONT, color: C.text, background: C.white, outline: 'none' }} />
                                <span style={{ fontSize: 12, color: C.mutedLight }}>—</span>
                                <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)}
                                    style={{ flex: 1, minWidth: 0, padding: '9px 10px', borderRadius: 8, border: `1.5px solid ${C.border}`, fontSize: 13, fontFamily: FONT, color: C.text, background: C.white, outline: 'none' }} />
                            </div>
                        )}
                    </div>
                ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', gap: 4, background: '#f1f5f9', borderRadius: 8, padding: 3 }}>
                        {categories.map(c => (
                            <button key={c.key} onClick={() => setActiveCategory(c.key)}
                                style={{
                                    padding: '6px 14px', borderRadius: 6, border: 'none',
                                    fontSize: 12, fontWeight: 600, cursor: 'pointer',
                                    fontFamily: FONT, transition: 'all 0.15s',
                                    background: activeCategory === c.key ? C.white : 'transparent',
                                    color: activeCategory === c.key ? C.green : C.muted,
                                    boxShadow: activeCategory === c.key ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                                }}>
                                {c.label}
                            </button>
                        ))}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
                        <div style={{ display: 'flex', gap: 4, background: '#f1f5f9', borderRadius: 8, padding: 3 }}>
                            {[
                                { key: 'today', label: t('voids_today') },
                                { key: 'week', label: t('voids_past_week') },
                                { key: 'month', label: t('voids_past_month') },
                                { key: 'year', label: t('voids_past_year') },
                                { key: 'custom', label: t('voids_custom') },
                            ].map(p => (
                                <button key={p.key} onClick={() => setDatePreset(p.key)}
                                    style={{
                                        padding: '6px 12px', borderRadius: 6, border: 'none',
                                        fontSize: 11, fontWeight: 600, cursor: 'pointer',
                                        fontFamily: FONT, transition: 'all 0.15s',
                                        background: datePreset === p.key ? C.white : 'transparent',
                                        color: datePreset === p.key ? C.green : C.muted,
                                        boxShadow: datePreset === p.key ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                                    }}>
                                    {p.label}
                                </button>
                            ))}
                        </div>
                        {datePreset === 'custom' && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)}
                                    style={{ padding: '6px 10px', borderRadius: 7, border: `1.5px solid ${C.border}`, fontSize: 12, fontFamily: FONT, color: C.text, background: C.white, outline: 'none' }} />
                                <span style={{ fontSize: 12, color: C.mutedLight }}>—</span>
                                <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)}
                                    style={{ padding: '6px 10px', borderRadius: 7, border: `1.5px solid ${C.border}`, fontSize: 12, fontFamily: FONT, color: C.text, background: C.white, outline: 'none' }} />
                            </div>
                        )}
                    </div>
                </div>
                )}

                {/* ─── Loading ──────────────────────────────────────────────────── */}
                {loading && <div style={{ textAlign: 'center', padding: 60, color: C.muted, fontSize: 13 }}>{t('loading')}...</div>}

                {/* ─── Empty ───────────────────────────────────────────────────── */}
                {!loading && voids.length === 0 && (
                    <div style={{ background: C.white, borderRadius: 12, border: `1.5px solid ${C.border}`, overflow: 'hidden' }}>
                        <EmptyState icon={noVoidsIcon} title={t('voids_empty_title')} subtitle={t('voids_empty_subtitle')} />
                    </div>
                )}

                {/* ─── Sections ─────────────────────────────────────────────────── */}
                {!loading && voids.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

                        {/* Financial */}
                        {(activeCategory === 'all' || activeCategory === 'financial') && (
                            <div style={{ background: C.white, borderRadius: 12, border: `1.5px solid ${C.border}`, overflow: 'hidden' }}>
                                <SectionHeader title={t('voids_financial')} count={financialVoids.length} collapsed={collapsedSections.financial} onToggle={() => toggleSection('financial')} />
                                {!collapsedSections.financial && (
                                    financialVoids.length === 0 ? (
                                        <EmptyState icon={financialIcon} title={t('voids_no_financial')} subtitle={t('voids_no_financial_desc')} />
                                    ) : isMobile ? (
                                        <div style={{ padding: 12 }}>
                                            {financialVoids.map(v => (
                                                <MobileVoidCard key={v.void_id} v={v} currency={currency} t={t} kind="financial" />
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="portal-table-wrap" style={{ overflowX: 'auto' }}>
                                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, fontFamily: FONT }}>
                                                <thead>
                                                    <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                                                        <th style={thStyle}>{t('voids_date')}</th>
                                                        <th style={thStyle}>{t('voids_type')}</th>
                                                        <th style={thStyle}>{t('voids_amount')}</th>
                                                        <th style={thStyle}>{t('voids_payment_method')}</th>
                                                        <th style={thStyle}>{t('voids_details')}</th>
                                                        <th style={thStyle}>{t('voids_authorized_by')}</th>
                                                        <th style={thStyle}>{t('voids_reason')}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {financialVoids.map(v => {
                                                        const tc = TYPE_COLOURS[v.void_type] || { color: C.muted, bg: '#f1f5f9' };
                                                        const rd = v.record_data || {};
                                                        const isExpanded = expandedRow === v.void_id;
                                                        return (
                                                            <React.Fragment key={v.void_id}>
                                                                <tr style={{ borderBottom: `1px solid ${C.border}`, ...rowClickStyle, background: isExpanded ? '#eff5ff' : 'transparent' }}
                                                                    onClick={() => toggleExpand(v.void_id)}
                                                                    onMouseEnter={e => { if (!isExpanded) e.currentTarget.style.background = '#f8fafc'; }}
                                                                    onMouseLeave={e => { if (!isExpanded) e.currentTarget.style.background = 'transparent'; }}>
                                                                    <td style={tdStyle}>{formatDate(v.deleted_at)}</td>
                                                                    <td style={tdStyle}><Badge color={tc.color} bg={tc.bg}>{t(`void_type_${v.void_type}`) || TYPE_LABELS[v.void_type]}</Badge></td>
                                                                    <td style={{ ...tdStyle, fontWeight: 600 }}>{fmtAmt(extractAmount(rd, v.void_type), currency)}</td>
                                                                    <td style={tdStyle}>{extractPaymentMethod(rd, v.void_type, t) || '—'}</td>
                                                                    <td style={{ ...tdStyle, maxWidth: 250 }} title={extractDetails(rd, v.void_type) || ''}>
                                                                        <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                            {extractDetails(rd, v.void_type) || '—'}
                                                                        </span>
                                                                    </td>
                                                                    <td style={tdStyle}>{v.authorized_by}</td>
                                                                    <td style={{ ...tdStyle, color: C.muted, fontStyle: v.reason ? 'normal' : 'italic' }}>
                                                                        {v.reason || t('voids_no_reason')}
                                                                    </td>
                                                                </tr>
                                                                {isExpanded && <tr><td colSpan={7} style={{ padding: 0 }}><VoidDetailPanel v={v} currency={currency} t={t} /></td></tr>}
                                                            </React.Fragment>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    )
                                )}
                            </div>
                        )}

                        {/* Operational */}
                        {(activeCategory === 'all' || activeCategory === 'operational') && (
                            <div style={{ background: C.white, borderRadius: 12, border: `1.5px solid ${C.border}`, overflow: 'hidden' }}>
                                <SectionHeader title={t('voids_operational')} count={operationalVoids.length} collapsed={collapsedSections.operational} onToggle={() => toggleSection('operational')} />
                                {!collapsedSections.operational && (
                                    operationalVoids.length === 0 ? (
                                        <EmptyState icon={operationalIcon} title={t('voids_no_operational')} subtitle={t('voids_no_operational_desc')} />
                                    ) : isMobile ? (
                                        <div style={{ padding: 12 }}>
                                            {operationalVoids.map(v => (
                                                <MobileVoidCard key={v.void_id} v={v} currency={currency} t={t} kind="operational" childTrackings={childTrackingMap[v.void_id] || []} />
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="portal-table-wrap" style={{ overflowX: 'auto' }}>
                                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, fontFamily: FONT }}>
                                                <thead>
                                                    <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                                                        <th style={thStyle}>{t('voids_date')}</th>
                                                        <th style={thStyle}>{t('voids_type')}</th>
                                                        <th style={thStyle}>{t('voids_dining_option')}</th>
                                                        <th style={thStyle}>{t('voids_table_order')}</th>
                                                        <th style={thStyle}>{t('voids_amount')}</th>
                                                        <th style={thStyle}>{t('voids_items')}</th>
                                                        <th style={thStyle}>{t('voids_deleted_by')}</th>
                                                        <th style={thStyle}>{t('voids_reason')}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {operationalVoids.map(v => {
                                                        const tc = TYPE_COLOURS[v.void_type] || { color: C.muted, bg: '#f1f5f9' };
                                                        const rd = v.record_data || {};
                                                        const isExpanded = expandedRow === v.void_id;
                                                        const children = childTrackingMap[v.void_id] || [];
                                                        return (
                                                            <React.Fragment key={v.void_id}>
                                                                <tr style={{ borderBottom: `1px solid ${C.border}`, ...rowClickStyle, background: isExpanded ? '#eff6ff' : 'transparent' }}
                                                                    onClick={() => toggleExpand(v.void_id)}
                                                                    onMouseEnter={e => { if (!isExpanded) e.currentTarget.style.background = '#f8fafc'; }}
                                                                    onMouseLeave={e => { if (!isExpanded) e.currentTarget.style.background = 'transparent'; }}>
                                                                    <td style={tdStyle}>{formatDate(v.deleted_at)}</td>
                                                                    <td style={tdStyle}>
                                                                        <Badge color={tc.color} bg={tc.bg}>{t(`void_type_${v.void_type}`) || TYPE_LABELS[v.void_type]}</Badge>
                                                                        {children.length > 0 && (
                                                                            <span style={{ fontSize: 10, color: C.muted, marginLeft: 6 }}>+{children.length} KDS</span>
                                                                        )}
                                                                    </td>
                                                                    <td style={tdStyle}>{extractDiningOption(rd, v.void_type) || '—'}</td>
                                                                    <td style={tdStyle}>{extractTableOrOrder(rd, v.void_type) || '—'}</td>
                                                                    <td style={{ ...tdStyle, fontWeight: 600 }}>{fmtAmt(extractAmount(rd, v.void_type), currency)}</td>
                                                                    <td style={{ ...tdStyle, maxWidth: 250 }} title={extractOperationalDetails(rd, v.void_type)}>
                                                                        <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                            {extractOperationalDetails(rd, v.void_type)}
                                                                        </span>
                                                                    </td>
                                                                    <td style={tdStyle}>{extractStaffName(rd, v.void_type) || v.authorized_by}</td>
                                                                    <td style={{ ...tdStyle, color: C.muted, fontStyle: v.reason ? 'normal' : 'italic' }}>
                                                                        {v.reason || t('voids_no_reason')}
                                                                    </td>
                                                                </tr>
                                                                {isExpanded && <tr><td colSpan={8} style={{ padding: 0 }}><VoidDetailPanel v={v} currency={currency} t={t} childTrackings={children} /></td></tr>}
                                                            </React.Fragment>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    )
                                )}
                            </div>
                        )}

                        {/* Staff */}
                        {(activeCategory === 'all' || activeCategory === 'staff') && (
                            <div style={{ background: C.white, borderRadius: 12, border: `1.5px solid ${C.border}`, overflow: 'hidden' }}>
                                <SectionHeader title={t('voids_staff')} count={staffVoids.length} collapsed={collapsedSections.staff} onToggle={() => toggleSection('staff')} />
                                {!collapsedSections.staff && (
                                    staffVoids.length === 0 ? (
                                        <EmptyState icon={staffIcon} title={t('voids_no_staff')} subtitle={t('voids_no_staff_desc')} />
                                    ) : (
                                        <div style={{ padding: '8px 0' }}>
                                            {staffVoids.map(v => {
                                                const tc = TYPE_COLOURS[v.void_type] || { color: C.muted, bg: '#f1f5f9' };
                                                const rd = v.record_data || {};
                                                const isExpanded = expandedRow === v.void_id;
                                                return (
                                                    <React.Fragment key={v.void_id}>
                                                        <div style={{
                                                            display: 'flex', alignItems: 'center', gap: 12,
                                                            padding: '10px 16px', borderBottom: `1px solid ${C.border}`,
                                                            fontSize: 13, fontFamily: FONT, cursor: 'pointer',
                                                            background: isExpanded ? '#fffbeb' : 'transparent',
                                                        }}
                                                            onClick={() => toggleExpand(v.void_id)}
                                                            onMouseEnter={e => { if (!isExpanded) e.currentTarget.style.background = '#f8fafc'; }}
                                                            onMouseLeave={e => { if (!isExpanded) e.currentTarget.style.background = isExpanded ? '#fffbeb' : 'transparent'; }}>
                                                            <span style={{ color: C.mutedLight, fontSize: 12, minWidth: 130 }}>{formatDate(v.deleted_at)}</span>
                                                            <Badge color={tc.color} bg={tc.bg}>{t(`void_type_${v.void_type}`) || TYPE_LABELS[v.void_type]}</Badge>
                                                            <span style={{ fontWeight: 600 }}>{extractEmployeeName(rd) || '—'}</span>
                                                            <span style={{ color: C.muted }}>{extractRole(rd, v.void_type) || '—'}</span>
                                                            <span style={{ marginLeft: 'auto', color: C.muted, fontStyle: v.reason ? 'normal' : 'italic', fontSize: 12 }}>
                                                                {v.reason || t('voids_no_reason')}
                                                            </span>
                                                        </div>
                                                        {isExpanded && <VoidDetailPanel v={v} currency={currency} t={t} />}
                                                    </React.Fragment>
                                                );
                                            })}
                                        </div>
                                    )
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* ─── Pagination ───────────────────────────────────────────────── */}
                {!loading && totalPages > 1 && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 20, fontFamily: FONT }}>
                        <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={paginationBtn(page > 1)}>{t('previous')}</button>
                        <span style={{ fontSize: 12, color: C.muted }}>{t('page')} {page} / {totalPages}</span>
                        <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={paginationBtn(page < totalPages)}>{t('next')}</button>
                    </div>
                )}
            </div>
        </PortalLayout>
    );
};

// ─── Style helpers ────────────────────────────────────────────────────────────
const thStyle = {
    textAlign: 'left', padding: '10px 14px', fontSize: 11, fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: 0.8, color: '#64748b',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
};

const tdStyle = {
    padding: '10px 14px', fontSize: 13, color: '#212529',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
};

const paginationBtn = (enabled) => ({
    padding: '6px 14px', borderRadius: 7, fontSize: 12, fontWeight: 600,
    border: `1.5px solid ${enabled ? '#e2e8f0' : '#f1f5f9'}`,
    background: enabled ? '#ffffff' : '#f8fafc',
    color: enabled ? '#212529' : '#94a3b8',
    cursor: enabled ? 'pointer' : 'default',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
});

export default PortalVoidsPage;
